import type { FC } from "react";

import Stepper from "@/components/shared/Stepper/Stepper.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import YourPace, {
  type YourPaceSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourPace.tsx";
import YourStay, {
  type YourStaySchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourStay.tsx";
import YourSanctuary, {
  type YourSanctuarySchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourSanctuary.tsx";
import YourTable, {
  type YourTableSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourTable.tsx";
import YourFoodPreferences, {
  type YourFoodPreferencesSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourFoodPreferences.tsx";
import YourWorld, {
  type YourWorldSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourWorld.tsx";
import YourPlans, {
  type YourPlansSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourPlans.tsx";
import YourSpending, {
  type YourSpendingSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourSpending.tsx";
import YourSplurges, {
  type YourSplurgesSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourSplurges.tsx";
import YourTrip, {
  type YourTripSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourTrip.tsx";
import YourDefaults, {
  type YourDefaultsSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourDefaults.tsx";
import YourAccessibility, {
  type YourAccessibilitySchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/YourAccessibility.tsx";
import AnythingElse, {
  type AnythingElseSchema,
} from "@/components/functional/TravelDnaManipulationForm/steps/AnythingElse.tsx";
import TravelDnaStep, {
  allTravelDnaSteps,
} from "@/_constants/TravelDnaStep.ts";
import type { DnaConsent, TravelDnaFormData } from "$/models/Dna.d.ts";

export type OnCompleteArgs =
  & {
    context: TravelDnaFormData;
    consent?: Pick<DnaConsent, 'page' | 'version' | 'status'>;
    skipped?: boolean;
  }
  & (
    | { step: typeof TravelDnaStep.YOUR_PACE; data: YourPaceSchema }
    | { step: typeof TravelDnaStep.YOUR_STAY; data: YourStaySchema }
    | {
      step: typeof TravelDnaStep.YOUR_SANCTUARY;
      data: YourSanctuarySchema;
    }
    | { step: typeof TravelDnaStep.YOUR_TABLE; data: YourTableSchema }
    | {
      step: typeof TravelDnaStep.YOUR_FOOD_PREFERENCES;
      data: YourFoodPreferencesSchema;
    }
    | { step: typeof TravelDnaStep.YOUR_WORLD; data: YourWorldSchema }
    | { step: typeof TravelDnaStep.YOUR_PLANS; data: YourPlansSchema }
    | {
      step: typeof TravelDnaStep.YOUR_SPENDING;
      data: YourSpendingSchema;
    }
    | {
      step: typeof TravelDnaStep.YOUR_SPLURGES;
      data: YourSplurgesSchema;
    }
    | { step: typeof TravelDnaStep.YOUR_TRIP; data: YourTripSchema }
    | {
      step: typeof TravelDnaStep.YOUR_DEFAULTS;
      data: YourDefaultsSchema;
    }
    | {
      step: typeof TravelDnaStep.YOUR_ACCESSIBILITY;
      data: YourAccessibilitySchema;
    }
    | { step: typeof TravelDnaStep.ANYTHING_ELSE; data: AnythingElseSchema }
  );

type TravelDnaManipulationFormProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
  initialData?: TravelDnaFormData;
  initialStep?: (typeof allTravelDnaSteps)[number];
  isLoadingStep?: boolean;
  endStep?: (typeof allTravelDnaSteps)[number];
  onEndStep?: () => void;
};

const TravelDnaManipulationForm: FC<TravelDnaManipulationFormProps> = ({
  onComplete,
  onCompleteWhenDirty,
  initialData,
  initialStep,
  isLoadingStep,
  endStep,
  onEndStep,
}) => {
  const { user } = useAuthContext();
  const currentStep = initialStep ?? allTravelDnaSteps[0];

  if (!currentStep) {
    console.error(`Invalid travel DNA step for user (${user?.id})`);
    return null;
  }

  const stepProps = {
    onComplete,
    onCompleteWhenDirty,
  };

  return (
    <Stepper.Wrapper
      steps={allTravelDnaSteps}
      isLoadingStep={isLoadingStep}
      initialContextData={initialData}
      initialStep={currentStep}
      endStep={endStep}
      onEndStep={onEndStep}
    >
      <Stepper.Step<typeof allTravelDnaSteps> step={TravelDnaStep.YOUR_PACE}>
        <YourPace {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps> step={TravelDnaStep.YOUR_STAY}>
        <YourStay {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps>
        step={TravelDnaStep.YOUR_SANCTUARY}
      >
        <YourSanctuary {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps> step={TravelDnaStep.YOUR_TABLE}>
        <YourTable {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps>
        step={TravelDnaStep.YOUR_FOOD_PREFERENCES}
      >
        <YourFoodPreferences {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps> step={TravelDnaStep.YOUR_WORLD}>
        <YourWorld {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps> step={TravelDnaStep.YOUR_PLANS}>
        <YourPlans {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps>
        step={TravelDnaStep.YOUR_SPENDING}
      >
        <YourSpending {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps>
        step={TravelDnaStep.YOUR_SPLURGES}
      >
        <YourSplurges {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps> step={TravelDnaStep.YOUR_TRIP}>
        <YourTrip {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps>
        step={TravelDnaStep.YOUR_DEFAULTS}
      >
        <YourDefaults {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps>
        step={TravelDnaStep.YOUR_ACCESSIBILITY}
      >
        <YourAccessibility {...stepProps} />
      </Stepper.Step>
      <Stepper.Step<typeof allTravelDnaSteps>
        step={TravelDnaStep.ANYTHING_ELSE}
      >
        <AnythingElse {...stepProps} />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default TravelDnaManipulationForm;
